/**
 * The chrome all five `/ai/*` routes share: the page header with its freshness
 * dot and refresh button, the sub-nav between the five routes, the loading
 * skeleton, the NOVA-is-down error branch, and the not-`ok` access panel.
 *
 * Collected here rather than repeated five times so a route cannot quietly ship
 * without the honesty affordances: `errors[]` beside the figures, the coverage
 * line, `Freshness` on a polling page, and an access panel instead of a strip of
 * zeros. A new `/ai/*` page gets them by construction.
 *
 * **Why the sub-nav is links and not `Tabs`.** The five routes are five URLs, not
 * five panels of one page: they have their own payloads, are separately
 * bookmarkable, and the browser's back button has to walk between them. The
 * ARIA `tab` pattern describes panels inside a single document and hijacking it
 * for navigation is a known accessibility anti-pattern — a screen-reader user is
 * told "tab, 2 of 5" and then the whole page changes underneath them. So this is
 * a `<nav>` of real anchors with `aria-current="page"`, which is what the pattern
 * is for. No DS component is being substituted; there is no route-switcher
 * component in the DS to substitute for.
 */

import { Icon, RefreshCw } from "@norican/icons";
import { Alert, Button, cn, Freshness, PageHeader, Skeleton } from "@norican/react";
import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";

import { logger } from "@/lib/logger";

import { AiAccessPanel, AiErrorsAlert } from "./access-panel";
import { AI_BUILT_PATHS, AI_ROUTES, isAiRouteBuilt } from "./params";
import { type AiPanelState, isPanelOk, staleAfterMs } from "./status";

/** The nav label the whole board answers to, used in the header title. */
const BOARD_TITLE = "AI";

interface AiSubNavProps {
  /** The route currently rendered, so exactly one link is `aria-current`. */
  pathname: string;
}

/** The row of links between the board's five routes. */
function AiSubNav({ pathname }: AiSubNavProps) {
  return (
    <nav aria-label="AI board sections" className="border-border-subtle -mb-px flex gap-1 border-b">
      {AI_ROUTES.filter((route) => isAiRouteBuilt(route.path)).map((route) => {
        const current = pathname === route.path;
        return (
          <Link
            key={route.path}
            to={route.path}
            aria-current={current ? "page" : undefined}
            className={cn(
              "rounded-t px-3 py-2 text-sm font-medium focus-visible:outline-2",
              // `text-accent`, not `text-accent-fg`: the latter is the foreground
              // painted ON an accent surface — white in the light theme — so it
              // vanishes against the page and the current tab reads as blank.
              current
                ? "border-accent text-accent border-b-2"
                : "text-fg-muted hover:text-fg border-b-2 border-transparent",
            )}
          >
            {route.label}
          </Link>
        );
      })}
    </nav>
  );
}

interface AiPageFrameProps {
  /** Which of the five routes this is — supplies the title suffix and subtitle. */
  path: string;
  /**
   * The payload whose state gates the page: `null`/`undefined` while loading,
   * a not-`ok` `access_state` renders the access panel instead of `children`.
   *
   * Omitted by a page whose content owns its own query — see `ownsItsQuery`.
   */
  panel?: AiPanelState | undefined;
  /** True while the query is in flight and there is nothing to show yet. */
  isPending?: boolean;
  /** True while any query on this page is refetching — drives the pulse. */
  isFetching?: boolean;
  /** A thrown error means the NOVA API is down, not Azure. */
  error?: unknown;
  /**
   * Set by a page whose single child owns its own query, access states and copy
   * — the Copilot and Claude pages, each reading a different upstream from the
   * Azure snapshot this frame was built around.
   *
   * It renders `children` unconditionally and skips the freshness dot, refresh
   * control and `errors[]` alert, because those describe the Azure snapshot and
   * this page has none. Without it the page would render blank: the normal path
   * gates children behind `panel &&`, and a page with no panel never passes.
   */
  ownsItsQuery?: boolean;
  /**
   * Extra `collected_at` values from other queries on the page (the capacity
   * page reads two), so the freshness dot reflects the page rather than whichever
   * query happens to be listed first.
   */
  extraCollectedAt?: readonly (string | null | undefined)[];
  /** Extra `errors[]` from other payloads on this page. */
  extraErrors?: readonly string[];
  /** The page's own content, rendered only when every gate passes. */
  children: ReactNode;
  /** Skeleton shape while the first response is in flight. */
  skeleton?: ReactNode;
}

export function AiPageFrame({
  path,
  panel,
  isPending = false,
  isFetching = false,
  error,
  ownsItsQuery = false,
  extraCollectedAt,
  extraErrors,
  children,
  skeleton,
}: AiPageFrameProps) {
  const queryClient = useQueryClient();
  const route = AI_ROUTES.find((candidate) => candidate.path === path);
  const location = useLocation();

  const collectedAt = [panel?.collected_at, ...(extraCollectedAt ?? [])]
    .filter((value): value is string => value != null)
    .sort((a, b) => b.localeCompare(a))[0];

  // De-duplicated: the extra payloads are projections of the same cached
  // snapshot, so each one carries the identical `errors[]` — rendered raw,
  // every warning would appear once per payload on the page.
  const errors = [...new Set([...(panel?.errors ?? []), ...(extraErrors ?? [])])];

  const refresh = () => {
    logger.debug("[ai] manual refresh requested", { path });
    void queryClient.invalidateQueries({ queryKey: ["ai"] });
  };

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title={route ? `${BOARD_TITLE} · ${route.label}` : BOARD_TITLE}
        subtitle={route?.subtitle}
        actions={
          <div className="flex items-center gap-4">
            {collectedAt && (
              <Freshness
                label="Azure read"
                since={collectedAt}
                staleAfter={staleAfterMs(panel?.cache_ttl_sec)}
                pulse={isFetching}
              />
            )}
            <Button
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={RefreshCw} size="sm" />}
              loading={isFetching}
              onClick={refresh}
            >
              Refresh
            </Button>
          </div>
        }
      />

      {/* One built route needs no switcher — a lone tab, or a row of links
          to routes that have no page, is worse than no nav at all. */}
      {AI_BUILT_PATHS.length > 1 && <AiSubNav pathname={location.pathname} />}

      {error != null && (
        <Alert tone="danger" title="Couldn't load the AI board">
          {error instanceof Error
            ? error.message
            : "The NOVA API did not respond. This is a NOVA problem, not an Azure one — every " +
              "Azure outcome, including a refused read and a throttle, arrives as a field on a 200."}
        </Alert>
      )}

      {/* A page that owns its own query renders straight through: there is no
          Azure panel to gate on, and its child already handles its own loading,
          access and error states with copy written for its upstream. */}
      {ownsItsQuery
        ? children
        : isPending && panel == null
          ? (skeleton ?? <AiPageSkeleton />)
          : panel && (
              <>
                {/* `errors[]` sits above the figures it undermines, not at the
                bottom of the page, and renders nothing when the list is empty. */}
                <AiErrorsAlert errors={errors} />

                {isPanelOk(panel) ? (
                  children
                ) : (
                  <AiAccessPanel panel={route?.label ?? BOARD_TITLE} state={panel} />
                )}
              </>
            )}
    </main>
  );
}

/** The default first-load shape: a KPI row over two panels. */
export function AiPageSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index} height="7rem" />
        ))}
      </div>
      <Skeleton height="20rem" />
      <Skeleton height="16rem" />
    </div>
  );
}
